import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { useAuth } from "../../utils/AuthContext";
import { url } from "../../Constant/TextConstant";
import Header from "../Header";
import Footer from "../Footer/Footer";
import { parse, isValid, format } from "date-fns";
import { encryptData } from "../../utils/encryption";
import { Helmet } from "react-helmet";
import { useUserLocation } from "../../utils/useUserLocation";
import "./ServiceBooking.css";

const ServiceBookingHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortOption, setSortOption] = useState("date-desc");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { currentUser } = useAuth();

   useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  useEffect(() => {
    const fetchServiceBookings = async () => {
      try {
        if (!currentUser) {
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${url.baseAPIUrl}/api/ServiceBooking/ViewByCustomerMobile/${currentUser}`
        );

        if (response.status === 200) {
          const data = response.data || [];
          setBookings(data);
          if (data.length === 0) {
            setSelectedCategory("services");
          }
        } else {
          throw new Error("Failed to load bookings");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceBookings();
  }, [currentUser]);

  const handleBookNow = () => setSelectedCategory("services");

  const getParsedDate = (dateStr) => {
    const parsed = parse(dateStr?.trim(), "MMM dd yyyy  h:mmaaa", new Date());
    return isValid(parsed) ? parsed : new Date(0);
  };

  const filteredBookings = bookings
    .filter((booking) => {
      const now = new Date();
      const date = getParsedDate(booking.DateTime);
      if (activeFilter === "Processing") {
        return (
          ["Processing", "Pending"].includes(booking.Status) && date >= now
        );
      }
      if (activeFilter === "Completed") return booking.Status === "Completed";
      if (activeFilter === "Rejected")
        return ["Rejected", "Reject"].includes(booking.Status);
      if (activeFilter === "Cancelled") return booking.Status === "Cancelled";
      return true;
    })
    .filter((booking) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        booking.ServiceName?.toLowerCase().includes(query) ||
        booking.ID?.toString().includes(query) ||
        booking.ProviderMobile?.includes(query) ||
        booking.Status?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      const dateA = getParsedDate(a.DateTime);
      const dateB = getParsedDate(b.DateTime);

      switch (sortOption) {
        case "date-asc":
          return dateA - dateB;
        case "date-desc":
          return dateB - dateA;
        case "name-asc":
          return (a.ServiceName || "").localeCompare(b.ServiceName || "");
        case "name-desc":
          return (b.ServiceName || "").localeCompare(a.ServiceName || "");
        case "status":
          return (a.Status || "").localeCompare(b.Status || "");
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading your bookings...</p>
      </div>
    );
  }

  if (error || !bookings.length) {
    return (
      <div className="empty-booking-container">
        <div className="empty-booking-icon">
          <svg viewBox="0 0 24 24">
            <path d="..." />
          </svg>
        </div>
        <h2>No Bookings Found</h2>
        <p>Explore our services and make your first booking today!</p>
        <button onClick={handleBookNow} className="book-now-button">
          Browse Services
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Your Service Bookings | Booking History</title>
        <meta
          name="description"
          content={`You have ${bookings.length} bookings.`}
        />
      </Helmet>

      <Header
        userLocation={currentLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <div className="service-booking-container">
        <header className="service-booking-header">
          <h1>Your Service Bookings</h1>
          <div className="booking-controls">
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="sort-select"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="name-asc">Service Name (A-Z)</option>
              <option value="name-desc">Service Name (Z-A)</option>
              <option value="status">Status</option>
            </select>
          </div>
          <div className="booking-summary">
            <span>{filteredBookings.length} Bookings</span>
            <div className="status-filters">
              {["All", "Processing", "Completed", "Rejected", "Cancelled"].map(
                (filter) => (
                  <button
                    key={filter}
                    className={`filter-button ${
                      activeFilter === filter ? "active" : ""
                    }`}
                    onClick={() => setActiveFilter(filter)}
                  >
                    {filter}
                  </button>
                )
              )}
            </div>
          </div>
        </header>

        <main className="booking-list">
          {filteredBookings.map((booking, index) => (
            <ServiceCard key={booking.ID} booking={booking} index={index} />
          ))}
        </main>
      </div>

      <Footer />
    </>
  );
};

const ServiceCard = ({ booking, index }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const getStatusInfo = (status) => {
    switch (status) {
      case "Processing":
      case "Pending":
        return { className: "status-processing", text: "Pending", icon: "⏳" };
      case "Confirmed":
        return { className: "status-confirmed", text: "Confirmed", icon: "✅" };
      case "Completed":
        return { className: "status-completed", text: "Completed", icon: "✔️" };
      case "Rejected":
      case "Reject":
        return { className: "status-rejected", text: "Rejected", icon: "❌" };
      case "Cancelled":
        return { className: "status-cancelled", text: "Cancelled", icon: "🚫" };
      default:
        return { className: "status-unknown", text: "Unknown", icon: "❓" };
    }
  };

  const bookingDate = parse(
    booking.DateTime,
    "MMM dd yyyy  h:mmaaa",
    new Date()
  );
  const safeDate = isValid(bookingDate) ? bookingDate : new Date();
  const formattedDate = format(safeDate, "EEE, MMM d, yyyy");
  const formattedTime = format(safeDate, "hh:mm a");

  const statusInfo = getStatusInfo(booking.Status);

  return (
    <div
      className={`booking-card animate-slide-in ${expanded ? "expanded" : ""}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="booking-card-content">
        <div className="booking-header">
          <div className="service-info">
            <div className="service-icon">
              {(booking.ServiceName || "?")[0]}
            </div>
            <div>
              <h3>{booking.ServiceName || "Unnamed Service"}</h3>
              <div className="booking-date">
                {formattedDate} • {formattedTime}
              </div>
            </div>
          </div>
          <div
            className={`booking-status ${statusInfo.className}`}
            onClick={() => setExpanded(!expanded)}
          >
            <span className="status-icon">{statusInfo.icon}</span>
            <span className="status-text">{statusInfo.text}</span>
            <span className="expand-icon">{expanded ? "▲" : "▼"}</span>
          </div>
        </div>
        {expanded && (
          <>
            <div className="booking-divider"></div>
            <div className="booking-details">
              <DetailRow label="Booking ID:" value={booking.ID} />
              <DetailRow label="Customer:" value={booking.CustomerMobile} />
              <DetailRow label="Provider:" value={booking.ProviderMobile} />
              <DetailRow label="Description:" value={booking.Description} />
              <div className="action-buttons">
                <button className="btn-service-booking"
                  onClick={() =>
                    navigate(
                      `/provider-details/${encryptData(booking.ProviderMobile)}`
                    )
                  }
                >
                  View Profile
                </button>
                <button className="btn-service-booking">Contact Provider</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value || "Not specified"}</span>
  </div>
);

export default ServiceBookingHistory;
